import { useKeycloak } from '@react-keycloak/web';
import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import { Button, Modal, Tabs, Tab, Alert, Form, Table, Spinner, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LoadingButton from '../../../../components/LoadingButton';
import { createCoriolisReportSessionApi, getCoriolisReportSessionApi, searchCoriolisCompaniesApi } from '../../disclosureApi';
import { useDispatch, useSelector } from 'react-redux';
import countryCodes from '../../../../utilities/countryCodes';
import { formatDate, isAdminRole, transErrorMessage, triggerFileDownload } from '../../../../utilities/common';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { config } from '../../../../utilities/config';
import { fetchCoriolisReports } from '../../disclosureSlice';
import DataTable from '../../../../components/DataTable';
import DownloadHelper from '../../../../components/DownloadHelper';
import { FiDownload } from 'react-icons/fi';

const GetStartedNow = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const isCanceled = searchParams.get('action') === 'canceled';
  const sessionId = searchParams.get('session_id');

  const keycloakObject = useKeycloak();
  const [show, setShow] = useState(false);
  const [activeTab, setActiveTab] = useState('view');
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [step, setStep] = useState('search');
  const orgId = useSelector((state) => state.users.user.usrOrgId);
  const coriolisReports = useSelector((state) => state.disclosure.coriolisReports);
  const [downloadReport, setDownloadReport] = useState(null);
  const CORIOLIS_COUNTRIES = [
    { code: 'AUS', label: t('Australia') },
    { code: 'GBR', label: t('United Kingdom') },
  ];
  const [countryCode, setCountryCode] = useState(
    CORIOLIS_COUNTRIES.find((c) => c.code === config.CORIOLIS_DEFAULT_COUNTRY)?.code ||
    CORIOLIS_COUNTRIES[0].code
  );

  const isAdmin = isAdminRole(keycloakObject);

  useImperativeHandle(ref, () => ({
    openModal: () => handleOpen(),
  }));

  const formatPrice = (val) => {
    const price = Number(val);
    return price && !isNaN(price) ? price.toFixed(2) : '...';
  };

  const handleOpen = () => {
    setActiveTab(isAdmin ? 'generate' : 'view');
    setShow(true);
    if (step !== 'verify') {
      setStep('search');
      setCompanies(null);
      setSearchTerm('');
      setSelectedCompany(null);
      setDownloadReport(null);
    }
  };
  const handleClose = () => setShow(false);

  useEffect(() => {
    dispatch(fetchCoriolisReports(orgId));
  }, [dispatch, orgId]);

  useEffect(() => {
    if (sessionId) {
      setActiveTab(isAdmin ? 'generate' : 'view');
      setShow((prev) => prev || true);
      setStep('verify');
      setLoading(true);
    }
  }, [sessionId, isAdmin]);

  useEffect(() => {
    if (isCanceled) {
      setActiveTab(isAdmin ? 'generate' : 'view');
      setShow((prev) => prev || true);
    }
  }, [isCanceled, isAdmin]);

  useEffect(() => {
    if (step !== 'verify' || !orgId || !sessionId || !show) return;
    let timeout;
    const checkStatus = async () => {
      try {
        const report = await getCoriolisReportSessionApi(orgId, sessionId);
        setDownloadReport(report);

        const setStatusAndStop = (status) => {
          setStep(status);
          setLoading(false);
          clearTimeout(timeout);
        };

        if (report.paymentStatus === 'failed') {
          setStatusAndStop('failed');
        } else if (report.paymentStatus === 'paid') {
          if (report?.downloadUrl) {
            setStatusAndStop('download');
            dispatch(fetchCoriolisReports(orgId));
          } else if (report?.uploadedFile?.upldflStatus === 'PROCESSING_FAILED') {
            setStatusAndStop('errored');
          } else {
            timeout = setTimeout(checkStatus, 10000);
          }
        } else {
          timeout = setTimeout(checkStatus, 10000);
        }
      } catch (err) {
        console.error('checkStatus failed:', err);
        toast.error(t('An error occurred while verifying your payment. Please refresh the page or contact the administrator.'));
        setLoading(false);
      }
    };

    checkStatus();

    return () => clearTimeout(timeout);
  }, [orgId, sessionId, t, step, show, dispatch]);

  const handleSearch = async () => {
    if (!searchTerm) return;
    setLoading(true);
    try {
      const data = await searchCoriolisCompaniesApi(orgId, { companyName: searchTerm, countryCode });
      setCompanies({ searchTerm, ...data });
    } catch (err) {
      toast.error(transErrorMessage(t, err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCheckout = async () => {
    setLoading(true);
    try {
      const data = await createCoriolisReportSessionApi(orgId, selectedCompany);
      window.location.href = data.url;
    } catch (err) {
      toast.error(transErrorMessage(t, err));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (report) => {
    triggerFileDownload(report.downloadUrl, report?.uploadedFile?.upldflName);
  };

  const renderSearchCompany = () => (
    <>
      <p>{t('Please search for the company you wish to download a report for:')}</p>
      <Form
        className="d-flex mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
      >
        <Form.Control
          type="text"
          placeholder={t('Enter company name')}
          value={searchTerm}
          disabled={loading}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="me-2 mr-2"
        />
        <Form.Control
          as="select"
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value)}
          disabled={loading}
          className="me-2 mr-2"
          style={{ maxWidth: '180px' }}
        >
          {CORIOLIS_COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>{t(c.label)}</option>
          ))}
        </Form.Control>
        <Button variant="primary" onClick={handleSearch} disabled={loading}>
          {t('Search')}
        </Button>
      </Form>

      {(!companies?.items?.length && companies?.searchTerm === searchTerm) && (
        <Alert variant="warning">
          <div>
            {t('The company you searched for does not appear in Coriolis ESG.')}
          </div>
        </Alert>
      )}

      {companies?.items?.length > 0 && (
        <Table striped bordered hover responsive size="sm">
          <thead>
            <tr>
              <th></th>
              <th>{t('Company Name')}</th>
              <th>{t('Registered Address')}</th>
              <th>{t('Country')}</th>
              <th>{t('Status')}</th>
            </tr>
          </thead>
          <tbody>
            {companies.items.slice(0, 10).map((c) => (
              <tr key={c.coriolisCompanyId}>
                <td style={{
                  textAlign: 'center',
                  paddingRight: 0
                }}>
                  <Form.Check
                    id={`ck-${c.coriolisCompanyId}`}
                    key={c.coriolisCompanyId}
                    type="radio"
                    name="selectedCompany"
                    checked={selectedCompany?.coriolisCompanyId === c.coriolisCompanyId}
                    onChange={() => setSelectedCompany(c)}
                  />
                </td>
                <td><label style={{ marginBottom: 0 }} for={`ck-${c.coriolisCompanyId}`} >{c.companyName}</label></td>
                <td>{c.registeredAddress}</td>
                <td>{countryCodes[c.countryCode] || c.countryCode}</td>
                <td>{c.status}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );

  const renderConfirmDisclaimer = () => (
    <div className="text-center p-2">
      <h4 className="mb-3 text-primary fw-bold">{t('Confirm Your Report Request')}</h4>
      <div className="border rounded-3 p-4 mb-3 shadow-sm">
        <p className="fs-5 mb-2">
          {t('You are about to purchase the ESG report for:')}
        </p>
        <p className="fs-4 fw-bold text-dark mb-3">
          <strong>{selectedCompany?.companyName || t('No company selected')}</strong>
        </p>

        <p className="fs-5 mb-1">
          💳 {t('Price per company report:')}{' '}
          <span className="fw-bold text-success">${formatPrice(companies?.pricing?.amount)} {companies?.pricing?.currency}</span>
        </p>
        <p className="text-muted small mb-0">
          {t('This one-time payment grants you access to download the complete ESG report for this company.')}
        </p>
      </div>

      <div className="text-start mt-4">
        <h6 className="fw-semibold">{t('Disclaimer')}</h6>
        <p className="text-muted small mb-0">
          {t('You will soon be redirected to a secure Stripe payment page. Please confirm that the company name above is correct before proceeding.')}
        </p>
      </div>
    </div>
  );

  const renderDownloadReportSummary = () => {
    return (
      <>
        <h4 className="mb-3 text-primary fw-bold">{t('Report Purchase Summary')}</h4>
        <div className="border rounded-3 p-4 mb-3 shadow-sm">
          <p className="fs-5 mb-2">
            {t('You have successfully requested the ESG report for:')}
          </p>
          <p className="fs-4 fw-bold text-dark mb-3">
            <strong>{downloadReport?.companyName || '...'}</strong>
          </p>
          <p className="fs-5 mb-1">
            💳 {t('Total Price:')}{' '}
            <span className="fw-bold text-success">${formatPrice(downloadReport?.amount)} {downloadReport?.currency}</span>
          </p>
          <p className="text-muted small mb-0">
            {t('This is a one-time purchase for the complete, downloadable ESG report.')}
          </p>
        </div>
      </>
    )
  };

  const renderDownloadReport = () => {
    const renderAlert = () => {
      if (step === 'download') {
        return (
          <Alert variant="success">
            <h5 className="fw-bold">{t('Your report is ready.')}</h5>
            <p className="mb-0">
              {t(
                'You can now download your complete report by clicking the button "Download Now" below, or access it anytime from the "View Reports" tab.'
              )}
            </p>
          </Alert>
        );
      }
      if (downloadReport?.paymentStatus === 'paid') {
        return (
          <Alert variant="info">
            <h5 className="fw-bold">{t('Payment Confirmed! Report is being generated.')}</h5>
            <p className="mb-0">
              {t(
                'Your payment was processed successfully. Your report is currently being generated and will be ready shortly. If you prefer not to wait, you can access and download it anytime from the "View Reports" tab.'
              )}
            </p>
          </Alert>
        );
      }
      return (
        <Alert variant="warning">
          <h5 className="fw-bold">{t('Verifying Your Transaction...')}</h5>
          <p className="mb-0">
            {t(
              'We are currently verifying your payment with the provider. This may take a few moments. Please keep this window open until confirmation is received.'
            )}
          </p>
        </Alert>
      );
    };
    return (
      <div className="text-center">
        {renderDownloadReportSummary()}
        {renderAlert()}
      </div>
    );
  };

  const renderReportErrored = () => (
    <div className="text-center">
      {renderDownloadReportSummary()}
      <Alert variant="danger">
        <h5 className="fw-bold">{t('Error Generating Report')}</h5>
        <p className="mb-0">
          {t(
            'An error occurred while generating your report. We apologize for the inconvenience. Please contact the administrator to resolve this as soon as possible.'
          )}
        </p>
      </Alert>
    </div>
  );

  const renderReportFailed = () => (
    <div className="text-center">
      {renderDownloadReportSummary()}
      <Alert variant="danger">
        <h5 className="fw-bold">{t('Payment Failed')}</h5>
        <p className="mb-0">
          {t(
            'Payment failed or could not be verified. Please try again later or contact support.'
          )}
        </p>
      </Alert>
    </div>
  );

  const renderGenerateTab = () => {
    switch (step) {
      case 'search':
        return renderSearchCompany();
      case 'confirm':
        return renderConfirmDisclaimer();
      case 'download':
      case 'verify':
        return renderDownloadReport();
      case 'failed':
        return renderReportFailed();
      case 'errored':
        return renderReportErrored();
      default:
        return null;
    }
  };

  const tableColumns = useMemo(
    () => [
      {
        Header: t('Company Name'),
        accessor: 'companyName'
      },
      {
        Header: t('Company Address'),
        accessor: 'registeredAddress',
        sortType: 'string'
      },
      {
        Header: t('Country'),
        sortType: 'string',
        accessor: (row) => {
          return countryCodes[row.countryCode] || row.countryCode;
        }
      },
      {
        Header: t('Generated At'),
        id: 'generatedAt',
        accessor: (row) => {
          return formatDate(row.reportGeneratedAt);
        }
      },
      {
        Header: '',
        accessor: 'actions',
        actionColumn: true,
        disableGlobalFilter: false,
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <div className="action-buttons">
              <DownloadHelper
                getLinkObject={async () => {
                  const report = await getCoriolisReportSessionApi(orgId, row.original.paymentSessionId);
                  return { objectUrl: report?.downloadUrl, fileName: report?.uploadedFile?.upldflName };
                }}
                label={''}
              />
            </div>
          );
        }
      }
    ],
    [t, orgId]
  );

  const renderViewTab = () => {
    if (coriolisReports === null) {
      return (
        <div className="text-center p-2">
          <Spinner animation="grow" />
        </div>
      );
    }
    if (coriolisReports?.length === 0) {
      return (
        <div className="text-center p-3 text-muted">
          {t('No Coriolis reports have been generated yet.')}
        </div>
      );
    }
    return (
      <>
        <Row>
          <Col>
            <DataTable
              columns={tableColumns}
              data={coriolisReports || []}
              pageLimit={5}
              defaultSort="generatedAt"
            />
          </Col>
        </Row>
        {!isAdmin && (
          <Alert variant="secondary">
            {t(
              'Only admin users can generate new Coriolis reports. You may download any existing reports above.'
            )}
          </Alert>
        )}
      </>
    );
  };

  const renderFooterButtons = () => {
    if (activeTab !== 'generate') return null;

    switch (step) {
      case 'search':
        return (
          <Button
            disabled={!selectedCompany || loading}
            onClick={() => setStep('confirm')}
            variant="primary"
          >
            {t('Download Report')}
          </Button>
        );
      case 'confirm':
        return (
          <Button disabled={loading} variant="primary" onClick={handleCreateCheckout}>
            {t('I Understand')}
          </Button>
        );
      case 'verify':
      case 'download':
        return (
          <Button
            disabled={loading || !downloadReport?.downloadUrl}
            variant="success"
            onClick={() => handleDownload(downloadReport)}
          >
            {t('Download Now')} <FiDownload className="ml-1" />
          </Button>
        );
      case 'failed':
        return (
          <Button variant="outline-danger" onClick={() => setStep('search')}>
            {t('Try Again')}
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{t('Coriolis ESG Reports')}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
            {isAdmin && (
              <Tab eventKey="generate" title={t('Generate Report')}>
                {renderGenerateTab()}
              </Tab>
            )}
            <Tab eventKey="view" title={t('View Reports')}>
              {renderViewTab()}
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <LoadingButton isLoading={loading} />
          {renderFooterButtons()}
          <Button variant="secondary" onClick={handleClose}>
            {t('Close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default GetStartedNow;
